import { Step } from 'react-joyride';
import { ExecutionCache } from './ExecutionCache';

export interface TourProps extends Omit<Step, 'target'> {
  id?: string;
  target?: string;
  active?: boolean;
}

export type RegisteredStep = Step & { id: string };

export function kebabCase(str: string): string {
  return str
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .replace(/[\s_]+/g, '-')
    .toLowerCase();
}

export class BaseTour extends ExecutionCache implements TourProps {
  id?: string;

  content: React.ReactNode;

  active: boolean = true;

  placement?: Step['placement'] = 'bottom';

  disableBeacon: boolean = false;

  requireAllSteps?: boolean;

  steps: { [key: string]: (...args: any[]) => TourProps } = {};

  tours: { [key: string]: (...args: any[]) => BaseTour } = {};

  private builtSteps: { [key: string]: RegisteredStep } = {};

  private builtTours: { [key: string]: BaseTour } = {};

  private listeners: (() => void)[] = [];

  constructor(props?: Partial<TourProps>) {
    super();
    if (props) {
      Object.assign(this, props);
    }
    this.builtSteps = Object.fromEntries(Object.keys(this.steps).map((key) => [key, undefined]));
    this.builtTours = Object.fromEntries(Object.keys(this.tours).map((key) => [key, undefined]));
  }

  subscribe(listener: () => void) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  unsubscribe(listener: () => void) {
    this.listeners = this.listeners.filter((l) => l !== listener);
  }

  private notify() {
    this.listeners.forEach((l) => l());
  }

  registerStep(step: keyof this['steps'], args: any[] = [], isVisible: boolean = true, idGen: ((id: string) => string) | null = null): string {
    return this.cachedExecution(
      `registerStep-${String(step)}`,
      () => {
        const key = String(step);
        const fullId = `${this.id}-${kebabCase(String(key))}`;
        const componentId = idGen ? idGen(fullId) : fullId;
        this.builtSteps[key] = isVisible ? { id: fullId, ...this.steps[key](...args), target: `#${componentId}` } : undefined;
        this.notify();
        return componentId;
      },
      args,
      isVisible,
      idGen ? JSON.stringify(idGen) : null
    );
  }

  registerTour<K extends keyof this['tours']>(tour: K, args: any[] = [], isVisible: boolean = true): ReturnType<this['tours'][K]> {
    return this.cachedExecution(
      `registerTour-${String(tour)}`,
      () => {
        const key = String(tour);
        const tourInstance = this.tours[key](...args) as ReturnType<this['tours'][K]>;
        tourInstance.active = this.active && isVisible;
        this.builtTours[key] = tourInstance;

        // Subscribe to sub-tour updates and bubble them up
        tourInstance.subscribe(() => {
          this.notify();
        });
        this.notify();
        return tourInstance;
      },
      args,
      isVisible
    );
  }

  genSteps(excludeIds: string[] = []): RegisteredStep[] {
    const { id, content, active, steps, tours, builtSteps, builtTours, listeners, ...tourProps } = this;
    if (!active) {
      return [];
    }
    // Iterate through keys in definition order to preserve step ordering
    const filteredSteps: RegisteredStep[] = Object.keys(this.steps)
      .map((key) => this.builtSteps[key])
      .filter((step) => {
        if (!step || excludeIds.includes(step.id)) {
          return false;
        }
        // Check if target element exists in DOM
        if (step.target && typeof step.target === 'string') {
          const element = document.querySelector(step.target);
          if (!element) {
            return false;
          }
        }
        return true;
      })
      .map((step) => ({
        ...tourProps,
        ...step,
      }));

    // disableBeacon: false allows react-floater to properly calculate position via beacon lifecycle
    return filteredSteps.map((step) => ({ ...step, disableBeacon: false }));
  }

  genTours(excludeIds: string[] = []): BaseTour[] {
    if (!this.active) {
      return [];
    }
    return Object.values(this.builtTours).filter((tour) => tour && tour.active && !excludeIds.includes(tour.id || '')) as BaseTour[];
  }

  getNextSteps(seenIds: string[] = []): { tour: BaseTour; steps: RegisteredStep[] | null } {
    const steps = this.genSteps(seenIds);
    if (steps.length !== 0) {
      return { tour: this, steps };
    }
    const tours = this.genTours(seenIds);
    if (tours.length === 0) {
      return { tour: this, steps: null };
    }
    for (const tour of tours) {
      const { tour: subTour, steps: subSteps } = tour.getNextSteps(seenIds);
      if (subSteps !== null) {
        return { tour: subTour, steps: subSteps };
      }
    }
    return { tour: this, steps: null };
  }
}
